import React, { useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  CircularProgress,
  alpha,
} from "@mui/material";
import { Feed } from "@mui/icons-material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "../../api";

import { FIZYOGRAFYA_OPTIONS } from "../../constants/fieldConstants";
import { getLandUseCode } from "../../utils/legendUtils";

// --- GÖRSEL YARDIMCI: KESİK ÇİZGİLİ ÇERÇEVE ---
const applyOuterFrame = (sheet, startRow, endRow, startCol, endCol) => {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const cell = sheet.getCell(r, c);
      const border = { ...(cell.border || {}) };
      if (r === startRow)
        border.top = { style: "mediumDashed", color: { argb: "FF000000" } };
      if (r === endRow)
        border.bottom = { style: "mediumDashed", color: { argb: "FF000000" } };
      if (c === startCol)
        border.left = { style: "mediumDashed", color: { argb: "FF000000" } };
      if (c === endCol)
        border.right = { style: "mediumDashed", color: { argb: "FF000000" } };
      cell.border = border;
    }
  }
};

// =====================================================================
// YARDIMCI COMPONENT 1: Detaylı Özellikler Tablosu (Ön Yüz)
// =====================================================================
const appendDetayliOzelliklerTablosu = (ws, startRow, d) => {
  const t = (deger, hedef, haric) => {
    if (!deger) return "□";
    const check = (v) => {
      const str = String(v).toLowerCase();
      const hasTarget = str.includes(hedef.toLowerCase());
      if (haric) return hasTarget && !str.includes(haric.toLowerCase());
      return hasTarget;
    };
    if (Array.isArray(deger)) return deger.some(check) ? "■" : "□";
    return check(deger) ? "■" : "□";
  };
  const landUseCode = getLandUseCode(d.arazi_kullanim_sinif);

  const bakiVal = String(d.egim_yonu || "")
    .toUpperCase()
    .trim();

  const isK =
    bakiVal.includes("KUZEY") ||
    bakiVal === "K" ||
    bakiVal.includes("KD") ||
    bakiVal.includes("KB");
  const isG =
    bakiVal.includes("GÜNEY") ||
    bakiVal.includes("GUNEY") ||
    bakiVal === "G" ||
    bakiVal.includes("GD") ||
    bakiVal.includes("GB");
  const isD =
    bakiVal.includes("DOĞU") ||
    bakiVal.includes("DOGU") ||
    bakiVal === "D" ||
    bakiVal.includes("KD") ||
    bakiVal.includes("GD");
  const isB =
    bakiVal.includes("BATI") ||
    bakiVal === "B" ||
    bakiVal.includes("KB") ||
    bakiVal.includes("GB");

  const renkK = isK ? "FFD32F2F" : "FFD9D9D9";
  const renkG = isG ? "FFD32F2F" : "FFD9D9D9";
  const renkD = isD ? "FFD32F2F" : "FFD9D9D9";
  const renkB = isB ? "FFD32F2F" : "FFD9D9D9";
  const renkMerkez = "FF000000";

  const checkDerinlik = (min, max) => {
    const val = d.tesirli_derinlik;
    if (val === null || val === undefined || val === "") return "□";
    const num = parseFloat(val);
    if (isNaN(num)) return "□";
    if (max === null) return num > min ? "■" : "□";
    return num >= min && num <= max ? "■" : "□";
  };

  const getPhysioCategory = (code) => {
    const option = FIZYOGRAFYA_OPTIONS.find((opt) => opt.code === code);
    if (!option) return "Ova";

    const label = option.label.toLowerCase();

    if (label.includes("dağ")) return "Dağ";
    if (label.includes("plato")) return "Plato";
    if (label.includes("tepe")) return "Tepe";
    if (label.includes("vadi")) return "Vadi Tabanı";
    if (label.includes("delta") || label.includes("ova")) return "Ova";
    if (label.includes("yamaç") && label.includes("dik")) return "Dik Yamaç";
    if (label.includes("yamaç")) return "Yamaç";
    if (label.includes("etek") && label.includes("alt")) return "Alt Etek";
    if (label.includes("etek")) return "Etek";
    if (label.includes("teras")) return "Teras";
    if (label.includes("çukur") || label.includes("depresyon"))
      return "Çukurluk";
    if (label.includes("taban")) return "Taban";
    if (
      label.includes("yatak") ||
      label.includes("nehir") ||
      label.includes("ırmak")
    )
      return "Nehir Yatağı";
    if (label.includes("yüksek")) return "Yüksek";

    return "Ova";
  };

  const getSlopeShapeCategory = (val) => {
    if (!val) return "Doğrusal";
    const s = String(val).toLowerCase();

    if (s.includes("doğrusal")) return "Doğrusal";
    if (s.includes("dış bükey") || s.includes("dis")) return "Dış Bükey";
    if (s.includes("hiperbolik")) return "Hiperbolik";
    if (s.includes("düzensiz") || s.includes("duzensiz")) return "Düzensiz";
    if (s.includes("iç bükey") || s.includes("ic")) return "İç Bükey";

    return "Doğrusal";
  };

  const currentPhysio = getPhysioCategory(d.fizyografya);
  const currentSlopeShape = getSlopeShapeCategory(d.fizyografya);

  const pc = (target) => (currentPhysio === target ? "■" : "□");
  const esc = (target) => (currentSlopeShape === target ? "■" : "□");
  const asc = (target) => (currentPhysio === target ? "■" : "□");

  const getMappedSlope = (rawSlope) => {
    if (!rawSlope) return "";
    const s = String(rawSlope).trim();
    if (s === "0-2") return "0-2";
    if (["2-4", "4-6", "2-6"].includes(s)) return "2-6";
    if (["6-8", "8-10", "10-12", "6-12"].includes(s)) return "6-12";
    if (["12-14", "14-16", "16-18", "18-20", "12-20"].includes(s))
      return "12-20";
    if (s === "20-30") return "20-30";
    if (s === "30-45") return "30-45";
    if (s === "45+" || s === ">45") return ">45";
    return s;
  };
  const mappedSlope = getMappedSlope(d.egim_yuzde);

  const headerFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { arg: "FFEAEAEA" },
  };
  let r = startRow;

  // --- Satır 0 ---
  ws.mergeCells(r, 1, r, 13);
  ws.getCell(r, 1).value = "Arazi Kullanımı";
  ws.mergeCells(r, 14, r, 24);
  ws.getCell(r, 16).value = "Ana Materyal Niteliği";
  ws.mergeCells(r, 25, r, 49);
  ws.getCell(r, 25).value = "Tesirli Toprak Derinliği (cm)";
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = "Yüzey Topografyası";
  [1, 16, 25, 50].forEach((c) => {
    ws.getCell(r, c).font = { bold: true, size: 8 };
    ws.getCell(r, c).alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell(r, c).fill = headerFill;
  });

  // --- Satır 1 ---
  r++;
  ws.mergeCells(r, 4, r, 5);
  ws.getCell(r, 4).value = `${landUseCode === "K" ? "■" : "□"}  K`;
  ws.mergeCells(r, 9, r, 10);
  ws.getCell(r, 9).value = `➔`;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "T" ? "■" : "□"} T`;

  ws.mergeCells(r, 14, r, 19);
  ws.getCell(r, 14).value = `${t(d.ana_materyal, "Aluviyal")} Aluviyal`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Volkan Külü")} Volkan Külü`;

  ws.mergeCells(r, 25, r, 33);
  ws.getCell(r, 25).value = `${checkDerinlik(150, null)} Çok Derin (150+)`;
  ws.mergeCells(r, 34, r, 41);
  ws.getCell(r, 34).value = `${checkDerinlik(91, 150)} Derin (91-150)`;
  ws.mergeCells(r, 42, r, 49);
  ws.getCell(r, 42).value = `${checkDerinlik(51, 90)} Orta Derin (51-90)`;

  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `(Rölyef)`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 2 ---
  r++;
  ws.mergeCells(r, 4, r, 5);
  ws.getCell(r, 4).value = `${landUseCode === "S" ? "■" : "□"}  S`;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "F" ? "■" : "□"} F`;

  ws.mergeCells(r, 14, r, 19);
  ws.getCell(r, 14).value = `${t(d.ana_materyal, "Koluviyal")} Koluviyal`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Lös")} Lös`;

  ws.mergeCells(r, 25, r, 36);
  ws.getCell(r, 25).value = `        ${checkDerinlik(21, 50)} Sığ (21-50)`;
  ws.mergeCells(r, 37, r, 49);
  ws.getCell(r, 37).value = `      ${checkDerinlik(0, 20)} Çok Sığ (0-20)`;

  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.yuzey_topografyasi, "Düz")} Düz`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 3 ---
  r++;
  ws.mergeCells(r, 4, r, 5);
  ws.getCell(r, 4).value = `${landUseCode === "Sy" ? "■" : "□"}  Sy`;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "H" ? "■" : "□"} H`;

  ws.mergeCells(r, 14, r, 19);
  ws.getCell(r, 14).value = `${t(d.ana_materyal, "Fluviyal")} Fluviyal`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Proklastik")} Proklastik`;

  ws.mergeCells(r, 25, r, 49);
  ws.getCell(r, 25).value =
    `Çevreye Göre Topoğrafik Konum : ${d.profil_nem || "..................."}`;
  ws.getCell(r, 25).font = { bold: true, size: 8 };

  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value =
    `${t(d.yuzey_topografyasi, "Hafif Dalgalı")} Hafif Dalgalı`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 4 ---
  r++;
  ws.mergeCells(r, 4, r, 5);
  ws.getCell(r, 4).value = `${landUseCode === "Ç" ? "■" : "□"}  Ç`;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "Z" ? "■" : "□"} Z`;

  ws.mergeCells(r, 14, r, 19);
  ws.getCell(r, 14).value = `${t(d.ana_materyal, "Organik")} Organik`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Marin")} Marin`;
  ws.mergeCells(r, 25, r, 49);
  ws.getCell(r, 25).value =
    "....................................................................................";

  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.yuzey_topografyasi, "Dalgalı")} Dalgalı`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 5 ---
  r++;
  ws.mergeCells(r, 4, r, 5);
  ws.getCell(r, 4).value = `${landUseCode === "B" ? "■" : "□"}  B`;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "N" ? "■" : "□"} N`;

  ws.mergeCells(r, 14, r, 19);
  ws.getCell(r, 14).value = `${t(d.ana_materyal, "Aeolian")} Aeolian`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Sabrolit")} Sabrolit`;

  ws.mergeCells(r, 25, r, 49);
  ws.getCell(r, 25).value =
    `Profilde Nem Durumu ve Belirtile : ${d.profil_nem || "..................."}`;
  ws.getCell(r, 25).font = { bold: true, size: 8 };

  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value =
    `${t(d.yuzey_topografyasi, "Çok Dalgalı")} Çok Dalgalı`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 6 ---
  r++;
  ws.mergeCells(r, 4, r, 5);
  ws.getCell(r, 4).value = `${landUseCode === "M" ? "■" : "□"}  M`;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "O" ? "■" : "□"} O`;

  ws.mergeCells(r, 14, r, 19);
  ws.getCell(r, 14).value = `${t(d.ana_materyal, "Lakustrin")} Lakustrin`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Karışık")} Karışık`;

  ws.mergeCells(r, 25, r, 49);
  ws.getCell(r, 25).value =
    "....................................................................................";
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.yuzey_topografyasi, "Kesik")} Kesik`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 7 ---
  r++;
  ws.mergeCells(r, 11, r, 12);
  ws.getCell(r, 11).value = `${landUseCode === "IY" ? "■" : "□"} IY`;

  ws.mergeCells(r, 14, r, 27);
  ws.getCell(r, 14).value =
    `Kaya Tipi : ${d.kaya_tipi || "................................"}`;
  ws.mergeCells(r, 28, r, 49);
  ws.getCell(r, 28).value =
    `Mikrorölyef: ${d.mikrorolyef || "................................"}`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.yuzey_topografyasi, "Ondüleli")} Ondüleli`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  // --- Satır 8 ---
  r++;

  ws.mergeCells(r, 14, r, 49);
  ws.getCell(r, 14).value = "Arazi Şekli (Fizyografya)";
  ws.getCell(r, 14).font = { bold: true, size: 8 };
  ws.getCell(r, 14).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 14).fill = headerFill;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.yuzey_topografyasi, "Diğer")} Diğer`;
  ws.getCell(r, 50).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  // --- Satır 9 ---
  r++;
  ws.mergeCells(r, 1, r, 7);
  ws.getCell(r, 1).value = "Eğim(%)";
  ws.getCell(r, 1).alignment = { horizontal: "center", vertical: "middle" };
  ws.mergeCells(r, 8, r, 13);
  ws.getCell(r, 8).value = "Eğim Yönü (Bakı)";
  ws.getCell(r, 8).alignment = { horizontal: "center", vertical: "middle" };

  ws.mergeCells(r, 14, r, 18);
  ws.getCell(r, 14).value = `${pc("Yüksek")} Yüksek`;

  ws.mergeCells(r, 19, r, 23);
  ws.getCell(r, 19).value = `${pc("Dik Yamaç")} Dik Yamaç`;

  ws.mergeCells(r, 24, r, 28);
  ws.getCell(r, 24).value = `${pc("Dağ")} Dağ`;

  ws.mergeCells(r, 29, r, 33);
  ws.getCell(r, 29).value = `${pc("Plato")} Plato`;

  ws.mergeCells(r, 34, r, 38);
  ws.getCell(r, 34).value = `${pc("Tepe")} Tepe`;

  ws.mergeCells(r, 39, r, 43);
  ws.getCell(r, 39).value = `${pc("Ova")} Ova`;

  ws.mergeCells(r, 44, r, 49);
  ws.getCell(r, 44).value = `${pc("Vadi Tabanı")} Vadi Tabanı`;

  [14, 19, 24, 29, 34, 39, 44].forEach((col) => {
    ws.getCell(r, col).font = { size: 8, name: "Calibri" };
  });
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = ".........................................";

  // --- Satır 10 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = `${t(mappedSlope, "0-2")} 0-2`;
  ws.mergeCells(r, 4, r, 7);
  ws.getCell(r, 4).value = `${t(mappedSlope, "20-30")} 20.1-30`;
  ws.mergeCells(r, 8, r + 4, 13);
  ws.getCell(r, 8).value = {
    richText: [
      { font: { bold: true, name: "Arial", size: 7 }, text: "K\n" },
      { font: { color: { argb: renkK }, size: 12 }, text: "▲\n" },
      { font: { bold: true, name: "Arial", size: 7 }, text: "B  " },
      { font: { color: { argb: renkB }, size: 16 }, text: "◄ " },
      { font: { color: { argb: renkMerkez }, size: 10 }, text: "╬" },
      { font: { color: { argb: renkD }, size: 16 }, text: " ►" },
      { font: { bold: true, name: "Arial", size: 7 }, text: "  D\n" },
      { font: { color: { argb: renkG }, size: 12 }, text: "▼\n" },
      { font: { bold: true, name: "Arial", size: 7 }, text: "G" },
    ],
  };
  ws.getCell(r, 8).alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };

  ws.mergeCells(r, 14, r, 18);
  ws.getCell(r, 14).value = `${pc("Nehir Yatağı")} Nehir Yatağı`;

  ws.mergeCells(r, 19, r, 23);
  ws.getCell(r, 19).value = `${pc("Çukurluk")} Çukurluk`;

  ws.mergeCells(r, 24, r, 28);
  ws.getCell(r, 24).value = `${pc("Teras")} Teras`;

  ws.mergeCells(r, 29, r, 33);
  ws.getCell(r, 29).value = `${pc("Taban")} Taban`;

  ws.mergeCells(r, 34, r, 38);
  ws.getCell(r, 34).value = `${pc("Etek")} Etek`;

  ws.mergeCells(r, 39, r, 43);
  ws.getCell(r, 39).value = `${pc("Alt Etek")} Alt Etek`;

  ws.mergeCells(r, 44, r, 49);
  ws.getCell(r, 44).value = `${pc("Yamaç")} Yamaç`;

  [14, 19, 24, 29, 34, 39, 44].forEach((col) => {
    ws.getCell(r, col).font = { size: 7.5, name: "Calibri" };
    ws.getCell(r, col).alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: false,
    };
  });
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = "Tuzluluk ve Alkalilik";
  ws.getCell(r, 50).font = { bold: true, size: 8 };
  ws.getCell(r, 50).alignment = { horizontal: "center" };
  ws.getCell(r, 50).fill = headerFill;

  // --- Satır 11 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = `${t(mappedSlope, "2-6")} 2.1-6`;
  ws.mergeCells(r, 4, r, 7);
  ws.getCell(r, 4).value = `${t(mappedSlope, "30-45")} 30.1-45`;

  ws.mergeCells(r, 14, r, 29);
  ws.getCell(r, 14).value = "Sel Basması";
  ws.mergeCells(r, 30, r + 1, 33);
  ws.getCell(r, 30).value = "Taban Suyu";
  ws.getCell(r, 30).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  ws.mergeCells(r, 34, r, 49);
  ws.getCell(r, 34).value = `En Düşük: ${d.taban_suyu_min || "......."} cm`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.tuzluluk, "Yok")} Tuzsuz`;

  // --- Satır 12 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = `${t(mappedSlope, "6-12")} 6.1-12`;
  ws.mergeCells(r, 4, r, 7);
  ws.getCell(r, 4).value = `${t(mappedSlope, ">45")} >45.1`;
  ws.mergeCells(r, 14, r, 29);
  ws.getCell(r, 14).value =
    `${t(d.sel_basmasi, "F1")} F1 ( arasıra sel alır. ekim zamanı gecikebilir)`;
  ws.mergeCells(r, 34, r, 49);
  ws.getCell(r, 34).value = `En Yüksek: ${d.taban_suyu_max || "......."} cm`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.tuzluluk, "Yok")} Hafif Tuzlu`;

  // --- Satır 13 ---
  r++;
  ws.mergeCells(r, 1, r, 7);
  ws.getCell(r, 1).value = `${t(mappedSlope, "12-20")} 12-20`;

  ws.mergeCells(r, 14, r, 29);
  ws.getCell(r, 14).value =
    `${t(d.sel_basmasi, "F2")} F2 (sıksık sel alır. mahsul sıksık zarar görür)`;
  ws.mergeCells(r, 30, r, 49);
  ws.getCell(r, 30).value =
    `Taban Suyu : ${d.taban_suyu || "................................"}`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.tuzluluk, "Yok")} Orta Tuzlu`;

  // --- Satır 14 ---
  r++;
  ws.mergeCells(r, 1, r, 7);
  ws.getCell(r, 1).value = `........................`;

  ws.mergeCells(r, 14, r, 29);
  ws.getCell(r, 14).value =
    `${t(d.sel_basmasi, "F3")} F3 (çok sık sel alır. Çok zarar. Ekonomik olmaz)`;
  ws.mergeCells(r, 30, r, 49);
  ws.getCell(r, 30).value =
    `Belirtileri : ${d.taban_suyu_belirti || "................................"}`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.tuzluluk, "Yok")} Çok Tuzlu`;

  // --- Satır 15 ---
  r++;
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 1).value = "Eğim Şekli";
  ws.mergeCells(r, 6, r, 14);
  ws.getCell(r, 8).value = "Arazi Şekli";

  [1, 8].forEach((c) => {
    ws.getCell(r, c).font = { bold: true, size: 8 };
    ws.getCell(r, c).alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell(r, c).fill = headerFill;
  });
  ws.mergeCells(r, 15, r, 28);
  ws.getCell(r, 15).value =
    `Kök Gel. Elverişli Top. Der.: ${d.kok_gel_derinlik || "...................."} cm.`;
  ws.mergeCells(r, 29, r, 56);
  ws.getCell(r, 29).value =
    `Kök Gel. Eng.Fak. Türü ve Der.: ${d.kok_gel_eng_faktor || "...................."}`;

  // --- Satır 16 ---
  r++;
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 1).value = `${esc("Doğrusal")} Doğrusal`;
  ws.mergeCells(r, 6, r, 10);
  ws.getCell(r, 6).value = `${asc("Dik Yamaç")} Dik Yamaç`;
  ws.mergeCells(r, 11, r, 14);
  ws.getCell(r, 11).value = `${asc("Ova")} Ova`;
  ws.mergeCells(r, 15, r, 24);
  ws.getCell(r, 15).value = "Tabii Toprak Drenajı";
  ws.getCell(r, 15).alignment = { vertical: "middle", horizontal: "center" };
  ws.mergeCells(r, 25, r, 37);
  ws.getCell(r, 25).value = "Yüzeydeki Çatlaklar (cm)";
  ws.getCell(r, 25).font = { bold: true, size: 8 };
  ws.getCell(r, 25).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 25).fill = headerFill;
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = "İnsan Etkisi";
  ws.getCell(r, 38).font = { bold: true, size: 8 };
  ws.getCell(r, 38).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 38).fill = headerFill;

  // --- Satır 17 ---
  r++;
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 1).value = `${esc("Dış Bükey")} Dış Bükey`;
  ws.mergeCells(r, 6, r, 10);
  ws.getCell(r, 6).value = `${asc("Dağ")} Dağ`;
  ws.mergeCells(r, 11, r, 14);
  ws.getCell(r, 11).value = `${asc("Çukurluk")} Çukurluk`;

  ws.mergeCells(r, 15, r, 24);
  ws.getCell(r, 15).value =
    `${t(d.tabii_drenaj, "yok")} Aşırı  ${t(d.tabii_drenaj, "yararlı")} İyi  ${t(d.tabii_drenaj, "f1")} Orta `;
  ws.getCell(r, 15).alignment = { vertical: "middle", horizontal: "center" };

  ws.mergeCells(r, 25, r, 37);
  ws.getCell(r, 25).value =
    `${t(d.yuzey_catlak, "İnce")} İnce (<1)   ${t(d.yuzey_catlak, "Orta")} Orta (1-2)   ${t(d.yuzey_catlak, "Geniş")} Geniş (2-5)`;
  ws.getCell(r, 25).alignment = { horizontal: "center", vertical: "middle" };
  ws.mergeCells(r, 38, r, 43);
  ws.getCell(r, 38).value = `${t(d.insan_etkisi, "Sürüm")} Sürüm`;
  ws.mergeCells(r, 44, r, 49);
  ws.getCell(r, 44).value = `${t(d.insan_etkisi, "Sulama")} Sulama`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.insan_etkisi, "Drenaj")} Drenaj`;

  // --- Satır 18 ---
  r++;
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 1).value = `${esc("Hiperbolik")} Hiperbolik`;
  ws.mergeCells(r, 6, r, 10);
  ws.getCell(r, 6).value = `${asc("Plato")} Plato`;
  ws.mergeCells(r, 11, r, 14);
  ws.getCell(r, 11).value = `${asc("Teras")} Teras`;

  ws.mergeCells(r, 15, r, 24);
  ws.getCell(r, 15).value =
    `  ${t(d.tabii_drenaj, "f2")} Yetersiz  ${t(d.tabii_drenaj, "f3")} Fena`;
  ws.getCell(r, 15).alignment = { vertical: "middle", horizontal: "center" };
  ws.mergeCells(r, 25, r, 37);
  ws.getCell(r, 25).value =
    `${t(d.yuzey_catlak, "Çok Geniş")} Çok Geniş (5-10)   ${t(d.yuzey_catlak, "Aşırı")} Aşırı geniş (>10)`;
  ws.getCell(r, 25).alignment = { horizontal: "center", vertical: "middle" };
  ws.mergeCells(r, 38, r, 43);
  ws.getCell(r, 38).value = `${t(d.insan_etkisi, "Gübreleme")} Gübreleme`;
  ws.mergeCells(r, 44, r, 49);
  ws.getCell(r, 44).value = `${t(d.insan_etkisi, "Sedde")} Sedde`;
  ws.mergeCells(r, 50, r, 56);
  ws.getCell(r, 50).value = `${t(d.insan_etkisi, "Teraslama")} Teraslama`;

  // --- Satır 19 ---
  r++;
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 1).value = `${esc("Düzensiz")} Düzensiz`;
  ws.mergeCells(r, 6, r, 10);
  ws.getCell(r, 6).value = `${asc("Vadi Tabanı")} Vadi Tabanı`;
  ws.mergeCells(r, 11, r, 14);
  ws.getCell(r, 11).value = `${asc("Tepe")} Tepe`;

  ws.mergeCells(r, 15, r, 24);
  ws.getCell(r, 15).value =
    `Yüzey Drenajı: ${d.yuzey_drenaji || "........................................"}`;
  ws.mergeCells(r, 25, r, 37);
  ws.getCell(r, 25).value = `Taşlılık - Çakıllılık - Kayalılık`;
  ws.mergeCells(r, 38, r, 43);
  ws.getCell(r, 38).value = `${t(d.insan_etkisi, "Tesviye")} Tesviye`;
  ws.mergeCells(r, 44, r, 56);
  ws.getCell(r, 44).value =
    `[ ] Diğer: ${!["Sürüm", "Sulama", "Drenaj", "Gübreleme", "Sedde", "Teraslama", "Tesviye"].includes(d.insan_etkisi) ? d.insan_etkisi || "" : "........"}`;

  // --- Satır 20 ---
  r++;
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 1).value = `${esc("İç Bükey")} İç Bükey`;
  ws.mergeCells(r, 6, r, 14);
  ws.getCell(r, 8).value = "";
  ws.mergeCells(r, 15, r, 37);
  ws.getCell(r, 15).value = "Taşlılık";
  ws.getCell(r, 15).font = { bold: true, size: 8 };

  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = `........................................`;

  // --- Satır 21 ---
  r++;
  ws.mergeCells(r, 1, r, 14);
  ws.getCell(r, 1).value = "Erozyon Türü";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.getCell(r, 1).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 1).fill = headerFill;
  ws.mergeCells(r, 15, r, 37);
  ws.getCell(r, 15).value =
    `${t(d.taslilik, "T1")} T1 Hafif Taşlı (arazi yüzeyinin ya da profilinin %2-10 kaplamış)`;

  // --- Satır 22 ---
  r++;
  ws.mergeCells(r, 1, r, 8);
  ws.getCell(r, 1).value = "Şekil:";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.mergeCells(r, 9, r, 14);
  ws.getCell(r, 9).value = "Derecesi:";
  ws.getCell(r, 9).font = { bold: true, size: 8 };

  ws.mergeCells(r, 15, r, 36);
  ws.getCell(r, 15).value =
    `${t(d.taslilik, "T2")} T2 Orta Taşlı (arazi yüzeyinin ya da profilinin %11-50 kaplamış)`;
  ws.mergeCells(r, 37, r, 56);
  ws.getCell(r, 37).value = `Kayalılık (Çapı>60cm)`;

  // --- Satır 23 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = `${t(d.erozyon_sekli, "Rüzgar")} Rüzgar Erozyonu`;
  ws.mergeCells(r, 10, r, 14);
  ws.getCell(r, 10).value = `${t(d.su_erozyon_derecesi, "Hafif")} Hafif`;

  ws.mergeCells(r, 15, r, 35);
  ws.getCell(r, 15).value =
    `${t(d.taslilik, "T3")} T3 Çok Taşlı (arazi yüzeyinin ya da profilinin %51-90 kaplamış)`;
  ws.mergeCells(r, 36, r, 56);
  ws.getCell(r, 36).value =
    `${t(d.kayalilik, "R0")} R0 Az Kayalı (arazi yüzeyinin %0-5 kaplamış)`;

  // --- Satır 24 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = `${t(d.erozyon_sekli, "Su")} Su Erozyonu`;
  ws.mergeCells(r, 10, r, 14);
  ws.getCell(r, 10).value = `${t(d.su_erozyon_derecesi, "Orta")} Orta`;
  ws.mergeCells(r, 36, r, 56);
  ws.getCell(r, 36).value =
    `${t(d.kayalilik, "R1")} R1 Hafif Kayalı (arazi yüzeyinin %6-10 kaplamış)`;

  // --- Satır 25 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = `    ${t(d.erozyon_sekli, "Yüzey")} Yüzey`;
  ws.mergeCells(r, 10, r, 14);
  ws.getCell(r, 10).value =
    `${t(d.su_erozyon_derecesi, "şiddetli", "çok")} Şiddetli`;
  ws.mergeCells(r, 15, r, 35);
  ws.getCell(r, 15).value = "Çakıllılık (Çapı 0,2-7,5cm)";
  ws.getCell(r, 15).font = { bold: true, size: 8 };
  ws.mergeCells(r, 36, r, 56);
  ws.getCell(r, 36).value =
    `${t(d.kayalilik, "R2")} R2 Orta Kayalı (arazi yüzeyinin %11-30 kaplamış)`;

  // --- Satır 26 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = `    ${t(d.erozyon_sekli, "Oluk")} Oluk`;
  ws.mergeCells(r, 10, r, 14);
  ws.getCell(r, 10).value =
    `${t(d.su_erozyon_derecesi, "çok şiddetli")} Çok Şiddetli`;
  ws.mergeCells(r, 15, r, 35);
  ws.getCell(r, 15).value =
    `${t(d.cakillilik, "Ç1")} Ç1 Hafif Taşlı (arazi yüzeyinin ya da profilinin %2-9 kaplamış)`;
  ws.mergeCells(r, 36, r, 56);
  ws.getCell(r, 36).value =
    `${t(d.kayalilik, "R3")} R3 Çok Kayalı  (arazi yüzeyinin %31-50 kaplamış)`;

  // --- Satır 27 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = `    ${t(d.erozyon_sekli, "Tünel")} Tünel`;
  ws.mergeCells(r, 10, r, 14);
  ws.getCell(r, 10).value = `      (yarıntılı)`;
  ws.mergeCells(r, 15, r, 35);
  ws.getCell(r, 15).value =
    `${t(d.cakillilik, "Ç2")} Ç2 Orta Taşlı (arazi yüzeyinin ya da profilinin %10-49 kaplamış)`;
  ws.mergeCells(r, 36, r, 56);
  ws.getCell(r, 36).value = `${t(d.kayalilik, "R4")} R4 Pek Çok Kayalı`;

  // --- Satır 28 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value =
    `    ${t(d.erozyon_sekli, "Sel Yarıntısı")} Sel Yarıntısı`;
  ws.mergeCells(r, 10, r, 14);
  ws.getCell(r, 10).value = "";
  ws.mergeCells(r, 15, r, 35);
  ws.getCell(r, 15).value =
    `${t(d.cakillilik, "Ç3")} Ç3 Çok Taşlı (arazi yüzeyinin ya da profilinin %51-90 kaplamış)`;
  ws.mergeCells(r, 36, r, 56);
  ws.getCell(r, 36).value = `(arazi yüzeyinin %51-90 kaplamış)`;

  // --- Satır 29: Taşkın Olma Olasılığı ---
  r++;
  ws.mergeCells(r, 1, r, 12);
  ws.getCell(r, 1).value = "Taşkın Olma Olasılığı:";
  ws.getCell(r, 1).font = { bold: true, size: 8, name: "Calibri" };

  ws.mergeCells(r, 13, r, 18);
  ws.getCell(r, 13).value = `${t(d.taskin_olasiligi, "Var")} Var`;

  ws.mergeCells(r, 19, r, 24);
  ws.getCell(r, 19).value = `${t(d.taskin_olasiligi, "Yok")} Yok`;

  ws.mergeCells(r, 25, r, 56);
  ws.getCell(r, 25).value =
    `Diğer: ${d.taskin_diger || "............................................................"}`;

  // --- Satır 30: Genel Düşünceler Başlık ---
  r++;
  ws.mergeCells(r, 1, r, 56);
  ws.getCell(r, 1).value = "Genel Düşünceleriniz";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.getCell(r, 1).alignment = { horizontal: "left", vertical: "middle" };
  ws.getCell(r, 1).fill = headerFill;

  // --- Satır 31-32: Genel Düşünceler İçerik ---
  const dusunceStartRow = r + 1;
  r += 2;
  ws.mergeCells(dusunceStartRow, 1, r, 56);
  ws.getCell(dusunceStartRow, 1).value = d.dusunceler || "";
  ws.getCell(dusunceStartRow, 1).alignment = {
    vertical: "top",
    horizontal: "left",
    wrapText: true,
  };

  // =========================================================
  // MATEMATİKSEL KUSURSUZ UYUM:
  // Sabit Hücre İçi Formatlama
  // =========================================================
  for (let rowIdx = startRow; rowIdx <= r; rowIdx++) {
    ws.getRow(rowIdx).height = 15.5;
    for (let colIdx = 1; colIdx <= 56; colIdx++) {
      const cell = ws.getCell(rowIdx, colIdx);
      if (!cell.font) cell.font = { name: "Calibri", size: 9 };
      if (!cell.alignment)
        cell.alignment = { vertical: "middle", horizontal: "left" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    }
  }

  // =========================================================
  // ÇERÇEVE ÇİZİCİ (GÖRSELDEKİ GİBİ KALIN SINIRLAR)
  // Koordinatlar hücrelere birebir hizalandı. SAKIN ELLEME!
  // =========================================================
  const drawBox = (r1, c1, r2, c2) => {
    for (let row = r1; row <= r2; row++) {
      for (let col = c1; col <= c2; col++) {
        const cell = ws.getCell(row, col);
        const border = { ...cell.border };
        if (row === r1) border.top = { style: "medium" };
        if (row === r2) border.bottom = { style: "medium" };
        if (col === c1) border.left = { style: "medium" };
        if (col === c2) border.right = { style: "medium" };
        cell.border = border;
      }
    }
  };

  const sr = startRow;

  drawBox(sr, 1, sr + 6, 13); // Arazi Kullanımı
  drawBox(sr, 14, sr + 6, 24); // Ana Materyal
  drawBox(sr, 25, sr + 2, 49); // Tesirli Toprak Derinliği
  drawBox(sr + 3, 25, sr + 4, 49); // Çevreye Göre Top. Konum
  drawBox(sr + 5, 25, sr + 6, 49); // Profil Nem
  drawBox(sr, 50, sr + 7, 56); // Yüzey Topografyası

  drawBox(sr + 7, 14, sr + 7, 27); // Kaya Tipi
  drawBox(sr + 7, 28, sr + 7, 49); // Mikrorölyef

  drawBox(sr + 8, 14, sr + 10, 49); // Arazi Şekli (Fizyografya)
  drawBox(sr + 8, 50, sr + 9, 56); // Yüzey Top. Alt Boşluk

  drawBox(sr + 9, 1, sr + 14, 7); // Eğim %
  drawBox(sr + 9, 8, sr + 14, 13); // Eğim Yönü (Bakı)

  drawBox(sr + 11, 14, sr + 14, 29); // Sel Basması
  drawBox(sr + 11, 30, sr + 14, 49); // Taban Suyu
  drawBox(sr + 10, 50, sr + 14, 56); // Tuzluluk

  drawBox(sr + 15, 15, sr + 15, 28); // Kök Gel. Elverişli Der.
  drawBox(sr + 15, 29, sr + 15, 56); // Kök Gel. Eng. Fak.

  drawBox(sr + 15, 1, sr + 20, 5); // Eğim Şekli
  drawBox(sr + 15, 6, sr + 20, 14); // Arazi Şekli Küçük

  drawBox(sr + 16, 15, sr + 18, 24); // Tabii Toprak Drenajı
  drawBox(sr + 19, 15, sr + 19, 24); // Yüzey Drenajı

  drawBox(sr + 16, 25, sr + 18, 37); // Yüzey Çatlakları
  drawBox(sr + 19, 25, sr + 19, 37); // Taşlılık-Çakıllılık-Kayalılık Başlık

  drawBox(sr + 16, 38, sr + 19, 56); // İnsan Etkisi

  drawBox(sr + 20, 15, sr + 20, 56); // Taşlılık Başlık Satırı

  drawBox(sr + 21, 1, sr + 28, 14); // Erozyon Türü
  drawBox(sr + 21, 15, sr + 28, 56); // Taşlılık-Çakıllılık İçerik

  drawBox(sr + 29, 1, sr + 29, 56); // Taşkın Olma Olasılığı
  drawBox(sr + 30, 1, sr + 32, 56); // Genel Düşünceler

  return r;
};

// =====================================================================
// YARDIMCI COMPONENT 2: Akıllı Horizon Seçici
// =====================================================================
const hlSmart = (visualLabels, dbValue, constantValues, sep) => {
  if (!dbValue || dbValue.length === 0) return visualLabels.join(sep);
  const dbValArr = Array.isArray(dbValue)
    ? dbValue.map((v) => String(v).trim())
    : [String(dbValue).trim()];
  return visualLabels
    .map((vLabel, i) => {
      const matchVal = constantValues[i]
        ? String(constantValues[i]).trim()
        : "";
      return dbValArr.some((v) => v === matchVal) ? `[ ${vLabel} ]` : vLabel;
    })
    .join(sep);
};

// =====================================================================
// ANA COMPONENT
// =====================================================================
export default function JobTemplateExportButton({ jobId, jobName }) {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/all-data");
      if (!res.data.success) throw new Error("Veriler alınamadı");

      const jobProfiles = res.data.data.filter((p) => p.job_id === jobId);

      if (jobProfiles.length === 0) {
        alert("Bu çalışmaya ait girilmiş profil bulunamadı.");
        setLoading(false);
        return;
      }

      jobProfiles.sort((a, b) => {
        const getNum = (str) => {
          const match = String(str || "").match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };
        return getNum(a.profil_no) - getNum(b.profil_no);
      });

      const workbook = new ExcelJS.Workbook();

      const ws = workbook.addWorksheet("Profiller ve Horizonlar", {
        views: [{ showGridLines: false, pageBreakPreview: true }],
        pageSetup: {
          paperSize: 9,
          orientation: "landscape",
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          margins: {
            left: 0.15,
            right: 0.15,
            top: 0.2,
            bottom: 0.2,
            header: 0,
            footer: 0,
          },
        },
      });

      ws.columns = Array.from({ length: 56 }, (_, i) => ({
        header: "",
        key: `col${i + 1}`,
        width: 2.8,
      }));

      // BAŞLIK BÖLÜMÜ İÇİN ÖZEL ÇERÇEVE FONKSİYONU
      const drawHeaderBox = (r1, c1, r2, c2) => {
        for (let row = r1; row <= r2; row++) {
          for (let col = c1; col <= c2; col++) {
            const cell = ws.getCell(row, col);
            const border = { ...cell.border };
            if (row === r1) border.top = { style: "medium" };
            if (row === r2) border.bottom = { style: "medium" };
            if (col === c1) border.left = { style: "medium" };
            if (col === c2) border.right = { style: "medium" };
            cell.border = border;
          }
        }
      };

      let currentStartRow = 2;

      jobProfiles.forEach((profile) => {
        let d = {};
        try {
          d =
            typeof profile.full_data === "string"
              ? JSON.parse(profile.full_data)
              : profile.full_data;
        } catch (e) {
          d = {};
        }

        // ==============================================================
        // BÖLÜM 1: PROFİL TABLOSU (ÖN YÜZ)
        // ==============================================================
        let r = currentStartRow;
        const blockStartRow = r;

        ws.mergeCells(r, 1, r, 17);
        ws.getCell(r, 1).value =
          `Çalışmanın Adı : ${d.calismanin_adi || jobName || "..............................................."}`;

        ws.mergeCells(r, 18, r, 25);
        ws.getCell(r, 25).value =
          `Profil No : ${profile.profil_no || "..........."}`;

        ws.mergeCells(r, 26, r, 47);
        ws.getCell(r, 35).value =
          `Tanımlayan : ${d.tanimlayan || "........................................................"}`;

        ws.mergeCells(r, 48, r, 56);
        ws.getCell(r, 50).value =
          `Tarih : ${d.tarih ? d.tarih.split("-").reverse().join(".") : "....../....../.........."}`;

        ws.mergeCells(r + 1, 1, r + 1, 17);
        ws.getCell(r + 1, 1).value =
          `..............................................................`;

        ws.mergeCells(r + 1, 18, r + 1, 31);
        ws.getCell(r + 1, 18).value =
          `İklim : ${d.iklim || "...................................."}`;

        ws.mergeCells(r + 1, 32, r + 1, 46);
        ws.getCell(r + 1, 32).value =
          `Yükseklik (rakım) : ${d.yukseklik ? d.yukseklik + " m." : "................... m."}`;

        ws.mergeCells(r + 1, 47, r + 1, 56);
        ws.getCell(r + 1, 47).value =
          `Harita No : ${d.harita_no || "...................."}`;

        ws.mergeCells(r + 2, 1, r + 2, 15);
        ws.getCell(r + 2, 1).value =
          `Toprak Serisi : ${d.toprak_serisi || ".............................................................................."}`;

        ws.mergeCells(r + 2, 16, r + 2, 40);
        ws.getCell(r + 2, 16).value =
          `Coğrafi Konum : ${d.yeri || "..................................................."}`;

        ws.mergeCells(r + 2, 41, r + 3, 45);
        const koordinatCell = ws.getCell(r + 2, 41);
        koordinatCell.value = "Koordinat";
        koordinatCell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        koordinatCell.font = { name: "Calibri", size: 9, bold: true };

        ws.mergeCells(r + 2, 46, r + 2, 56);
        ws.getCell(r + 2, 46).value =
          `X : ${d.koordinat_x || "................."}`;

        ws.mergeCells(r + 3, 1, r + 3, 7);
        ws.getCell(r + 3, 1).value =
          `Ort. Sıcaklık : ${d.ort_sicaklik ? d.ort_sicaklik + " °C" : "....... °C"}`;

        ws.mergeCells(r + 3, 8, r + 3, 20);
        ws.getCell(r + 3, 8).value =
          `Ort. Toplam Yağış : ${d.ort_yagis ? d.ort_yagis + " mm." : ".............. mm."}`;

        ws.mergeCells(r + 3, 21, r + 3, 40);
        ws.getCell(r + 3, 21).value =
          `Yeri : ${d.il || ""} ${d.ilce || "...................................................................."}`;

        ws.mergeCells(r + 3, 46, r + 3, 56);
        ws.getCell(r + 3, 46).value =
          `Y : ${d.koordinat_y || "................."}`;

        // BAŞLIK BİLGİLERİ SATIR YÜKSEKLİĞİ (Sayfa Dengesi İçin)
        for (let i = 0; i <= 3; i++) {
          const rowIdx = r + i;
          ws.getRow(rowIdx).height = 18; // Başlık satırları biraz daha yüksek
          for (let c = 1; c <= 56; c++) {
            const cell = ws.getCell(rowIdx, c);
            if (cell.address !== ws.getCell(r + 2, 46).address) {
              cell.font = { name: "Calibri", size: 10, bold: true };
              cell.alignment = {
                vertical: "middle",
                horizontal: "left",
                wrapText: true,
              };
            }
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }
        }

        // BAŞLIK BÖLÜMÜNÜN KALIN ÇERÇEVELERİ
        drawHeaderBox(r, 1, r + 3, 56);
        drawHeaderBox(r + 2, 41, r + 3, 56); // Koordinat kutusu

        let altTabloStart = r + 4;
        const lastRowUsed = appendDetayliOzelliklerTablosu(
          ws,
          altTabloStart,
          d,
        );

        // ==============================================================
        // --- SAYFA KESMESİ (SAYFA 1 SONU) ---
        // ==============================================================
        try {
          ws.getRow(lastRowUsed).addPageBreak();
        } catch (e) {}

        // ==============================================================
        // BÖLÜM 2: HORİZON TABLOSU (ARKA YÜZ - 2. SAYFA)
        // HORİZONA HİÇ DOKUNULMADI (Orijinal Yüksekliğinde Bırakıldı)
        // ==============================================================
        let hr = lastRowUsed + 2;

        ws.mergeCells(hr, 1, hr, 56);
        const hTitle = ws.getCell(hr, 1);
        hTitle.value = `PROFİL NO: ${profile.profil_no || "Belirtilmemiş"} - HORİZON BİLGİLERİ (ARKA YÜZ)`;
        hTitle.font = { bold: true, size: 12 };
        hTitle.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { arg: "FFEAEAEA" },
        };
        hTitle.alignment = { vertical: "middle", horizontal: "left" };
        ws.getRow(hr).height = 18;
        hr++;

        const hCols = [
          [1, 4, "HOR.", true],
          [5, 8, "DER. (cm)**", true],
          [9, 14, "SINIR", false],
          [15, 18, "RENK", true],
          [19, 23, "TEKSTÜR", true],
          [24, 30, "STRÜKTÜR", true],
          [31, 37, "KIVAM", true],
          [38, 41, "KİREÇ\n(köpürme)", true],
          [42, 44, "TAŞ.", true],
          [45, 53, "KÖK DAĞ.", false],
          [54, 56, "ÖZEL GÖRÜNÜMLER", true],
        ];

        hCols.forEach((col) => {
          if (col[3]) {
            ws.mergeCells(hr, col[0], hr + 1, col[1]);
          } else {
            ws.mergeCells(hr, col[0], hr, col[1]);
          }
          const c = ws.getCell(hr, col[0]);
          c.value = col[2];
          c.font = { bold: true, size: 8 };
          c.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
          };
        });

        ws.mergeCells(hr + 1, 9, hr + 1, 11);
        ws.getCell(hr + 1, 9).value = "Belirginlik";
        ws.mergeCells(hr + 1, 12, hr + 1, 14);
        ws.getCell(hr + 1, 12).value = "Şekil";
        ws.mergeCells(hr + 1, 45, hr + 1, 47);
        ws.getCell(hr + 1, 45).value = "ÇEŞİT";
        ws.mergeCells(hr + 1, 48, hr + 1, 50);
        ws.getCell(hr + 1, 48).value = "MİKTAR";
        ws.mergeCells(hr + 1, 51, hr + 1, 53);
        ws.getCell(hr + 1, 51).value = "KALINLIK";

        [hr, hr + 1].forEach((rowIdx) => {
          ws.getRow(rowIdx).height = 16;
          for (let ci = 1; ci <= 56; ci++) {
            const cell = ws.getCell(rowIdx, ci);
            if (!cell.font) cell.font = { bold: true, size: 8 };
            if (!cell.alignment)
              cell.alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true,
              };
            cell.border = {
              top: { style: "thick" },
              bottom: { style: "thick" },
              left: { style: "thin" },
              right: { style: "thin" },
            };
          }
        });

        hr += 2;
        const hBlockStart = hr;
        const horizons = d.horizons || [];

        if (horizons.length === 0) {
          ws.mergeCells(hr, 1, hr, 56);
          ws.getCell(hr, 1).value =
            "Bu profil için girilmiş horizon verisi bulunamadı.";
          ws.getCell(hr, 1).alignment = { horizontal: "center" };
          hr++;
        } else {
          horizons.forEach((h) => {
            const sinirBelText = hlSmart(
              ["keskin", "belirli", "geçişli", "yaygın"],
              h.sinir_belirginlik,
              ["Keskin", "Belirli", "Geçişli", "Yaygın"],
              "\n",
            );
            const sinirSekilText = hlSmart(
              ["düz", "dalgalı", "düzensiz", "kırıklı"],
              h.sinir_sekil,
              ["Düz", "Dalgalı", "Düzensiz", "Kırıklı"],
              "\n",
            );
            const teksturText = hlSmart(
              [
                "C",
                "CL",
                "FLS",
                "L",
                "LFS",
                "LS",
                "S",
                "SC",
                "SCL",
                "SIC",
                "SICL",
                "SIL",
                "SL",
                "VFLS",
              ],
              h.tekstur,
              [
                "C (Kil)",
                "CL (Killi Tın)",
                "FLS (İnce Tınlı Kum)",
                "L (Tın)",
                "LFS (Tınlı İnce Kum)",
                "LS (Tınlı Kum)",
                "S (Kum)",
                "SC (Kumlu Kil)",
                "SCL (Kumlu Killi Tın)",
                "SIC (Siltli Kil)",
                "SICL (Siltli Killi Tın)",
                "SIL (Siltli Tın)",
                "SL (Kumlu Tın)",
                "VFLS (Çok İnce Tınlı Kum)",
              ],
              " - ",
            );

            const strDayText = `DAY: ${hlSmart(["ç.zayıf", "zayıf", "orta", "kuvvetli", "ç.kuvvetli"], h.struktur_dayaniklilik, ["Çok Zayıf", "Zayıf", "Orta", "Kuvvetli", "Çok Kuvvetli"], " - ")}`;
            const strBuyText = `BÜY: ${hlSmart(["ç.küçük", "küçük", "orta", "kaba", "ç.kaba"], h.struktur_buyukluk, ["Çok Küçük / Çok İnce", "Küçük / İnce", "Orta", "Kaba / Kalın", "Çok Kaba / Çok Kalın"], " - ")}`;
            const strTipText = `TİP: ${hlSmart(["furda", "granüler", "levha", "priz.kol.", "sütunsu", "köş.blok", "yr.köş.blok", "masif", "taneli"], h.struktur_tip, ["Furda", "Granüler", "Levha", "Prizmatik", "Sütunsu", "Köşeli Blok", "Yarı Köşeli Blok", "Masif", "Taneli"], " - ")}`;
            const strukturAll = `${strDayText}\n${strBuyText}\n${strTipText}`;

            const kivamKuruText = `K: ${hlSmart(["dağınık", "yumuşak", "h.sert", "sert", "çok sert"], h.kivam_kuru, ["Dağınık", "Yumuşak", "Hafif Sert", "Sert", "Çok Sert"], " - ")}`;
            const kivamNemliText = `N: ${hlSmart(["dağınık", "gevşek", "sıkı", "çok sıkı", "aşırı sıkı"], h.kivam_nemli, ["Dağınık", "Gevşek", "Sıkı", "Çok Sıkı", "Aşırı Sıkı"], " - ")}`;
            const kYasYapText = hlSmart(
              ["yap.değil", "az yap.", "yap.", "ç.yap."],
              h.kivam_yas_yapiskanlik,
              ["Yapışkan Değil", "Az Yapışkan", "Yapışkan", "Çok Yapışkan"],
              " - ",
            );
            const kYasPlasText = hlSmart(
              ["plas. değ.", "az plas.", "plastik", "ç.plastik"],
              h.kivam_yas_plastiklik,
              ["Plastik Değil", "Az Plastik", "Plastik", "Çok Plastik"],
              " - ",
            );
            const kivamAll = `${kivamKuruText}\n${kivamNemliText}\nY: ${kYasYapText} / ${kYasPlasText}`;

            const kirecText = hlSmart(
              ["Hiç Yok", "Görünebilir", "Zayıf", "Kuvvetli"],
              h.kirec,
              ["Hiç Yok", "Görünebilir", "Zayıf", "Kuvvetli"],
              "\n",
            );
            const tasText = hlSmart(
              ["taşsız", "az taşlı", "taşlı", "çok taşlı"],
              h.taslilik,
              ["Taşsız", "Az Taşlı", "Taşlı", "Çok Taşlı"],
              "\n",
            );

            const kCesitArr = [];
            if (h.kok_sacak_miktar && h.kok_sacak_miktar !== "Yok")
              kCesitArr.push("Saçak");
            if (h.kok_kazik_miktar && h.kok_kazik_miktar !== "Yok")
              kCesitArr.push("Kazık");
            const cesitText = hlSmart(
              ["saçak", "kazık"],
              kCesitArr,
              ["Saçak", "Kazık"],
              "\n\n",
            );

            const mMik1 = hlSmart(
              ["ç.sey.", "seyrek", "yaygın", "ç.yaygın"],
              h.kok_sacak_miktar,
              ["Çok Seyrek", "Seyrek", "Yaygın", "Çok Yaygın"],
              "\n",
            );
            const mMik2 = hlSmart(
              ["ç.sey.", "seyrek", "yaygın", "ç.yaygın"],
              h.kok_kazik_miktar,
              ["Çok Seyrek", "Seyrek", "Yaygın", "Çok Yaygın"],
              "\n",
            );
            const miktarText = `${mMik1}\n---\n${mMik2}`;

            const mKal1 = hlSmart(
              ["ç.ince", "ince", "orta", "kaba", "ç.kaba"],
              h.kok_sacak_kalinlik,
              ["Çok İnce", "İnce", "Orta", "Kaba", "Çok Kaba"],
              "\n",
            );
            const mKal2 = hlSmart(
              ["ç.ince", "ince", "orta", "kaba", "ç.kaba"],
              h.kok_kazik_kalinlik,
              ["Çok İnce", "İnce", "Orta", "Kaba", "Çok Kaba"],
              "\n",
            );
            const kalinlikText = `${mKal1}\n---\n${mKal2}`;

            ws.mergeCells(hr, 1, hr, 4);
            ws.getCell(hr, 1).value = h.sembol || "";
            ws.mergeCells(hr, 5, hr, 8);
            ws.getCell(hr, 5).value =
              `${h.derinlik_min || 0} - ${h.derinlik_max || ""}${h.derinlik_arti ? "+" : ""}`;
            ws.mergeCells(hr, 9, hr, 11);
            ws.getCell(hr, 9).value = sinirBelText;
            ws.mergeCells(hr, 12, hr, 14);
            ws.mergeCells(hr, 15, hr, 18);
            ws.getCell(hr, 15).value =
              `Kuru:\n${h.renk_sinif_kuru || ""} ${h.renk_kod_kuru || ""}\nNemli:\n${h.renk_sinif_yas || ""} ${h.renk_kod_yas || ""}`;
            ws.mergeCells(hr, 19, hr, 23);
            ws.getCell(hr, 19).value = teksturText;
            ws.mergeCells(hr, 24, hr, 30);
            ws.getCell(hr, 24).value = strukturAll;
            ws.mergeCells(hr, 31, hr, 37);
            ws.getCell(hr, 31).value = kivamAll;
            ws.mergeCells(hr, 38, hr, 41);
            ws.getCell(hr, 38).value = kirecText;
            ws.mergeCells(hr, 42, hr, 44);
            ws.getCell(hr, 42).value = tasText;
            ws.mergeCells(hr, 45, hr, 47);
            ws.getCell(hr, 45).value = cesitText;
            ws.mergeCells(hr, 48, hr, 50);
            ws.getCell(hr, 48).value = miktarText;
            ws.mergeCells(hr, 51, hr, 53);
            ws.getCell(hr, 51).value = kalinlikText;
            ws.mergeCells(hr, 54, hr, 56);
            ws.getCell(hr, 54).value = h.ozel_gorunum || "";

            ws.getRow(hr).height = 70;
            for (let ci = 1; ci <= 56; ci++) {
              const cell = ws.getCell(hr, ci);
              cell.font = { size: 7.5, name: "Calibri" };
              cell.alignment = {
                wrapText: true,
                vertical: "middle",
                horizontal: "center",
              };
              cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
              };
            }
            hr++;
          });
        }

        applyOuterFrame(ws, hBlockStart - 2, hr - 1, 1, 56);

        try {
          ws.getRow(hr).addPageBreak();
        } catch (e) {}

        currentStartRow = hr + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${jobName}_A4_Yatay_Cikti.xlsx`);
    } catch (error) {
      console.error("Form Export Hatası:", error);
      alert("Rapor indirilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Tooltip title="Formu İndir (Yatay A4 - Baskıya Hazır)">
        <IconButton
          onClick={handleExport}
          disabled={loading}
          sx={{
            bgcolor: alpha("#009688", 0.1),
            color: "#009688",
            mb: 0.5,
            "&:hover": { bgcolor: alpha("#009688", 0.2) },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Feed fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{ fontSize: "0.6rem", color: "text.secondary", fontWeight: "bold" }}
      >
        P-ŞABLON-2
      </Typography>
    </Box>
  );
}
